import { useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, Mail, Building2, MapPin} from "lucide-react";
import { motion } from "framer-motion";
import "./LandingPage.css";
import LandingNavbar from "./LandingNavbar";
import "./AboutUsPage.css";

function Btn({ variant = "solid", size = "md", className = "", ...props }) {
  const v =
    variant === "solid"
      ? "lp-btn lp-btn--solid"
      : variant === "outline"
      ? "lp-btn lp-btn--outline"
      : "lp-btn lp-btn--ghost";
  const s = size === "lg" ? "lp-btn--lg" : "";
  return <button className={`${v} ${s} ${className}`} {...props} />;
}

export default function AboutUsPage() {
  const { t } = useTranslation();

  // Ensure scrolling is disabled on this specific page
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="lp about-viewport-lock">
      <LandingNavbar />

      <section className="lp-hero about-hero-adjust">
        <div className="lp-hero-slide">
          <motion.div
            className="lp-hero-bg-wrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <img src="/images/olympeak_visual_2.png" alt="" className="lp-hero-bg" />
            <div className="lp-hero-overlay tone-pink-high" />
          </motion.div>

          <div className="lp-hero-content about-content-flex">
            {/* Top Section: Hero Text */}
            <motion.div
              className="about-hero-top"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="lp-hero-h1">
                <Trans
                  i18nKey="about.hero.title"
                  components={{ pink: <span className="lp-pink" /> }}
                />
              </h1>
              <p className="lp-hero-lead">{t("about.hero.lead")}</p>
              <div className="lp-hero-actions">
                <a href="/auth?mode=register">
                  <Btn size="lg" className="lp-glow-btn">
                    {t("about.cta.startFree")} <ArrowRight size={18} />
                  </Btn>
                </a>
              </div>
            </motion.div>

            {/* Bottom Section: Integrated Mission & Contact */}
            <motion.div 
              className="about-details-grid"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="about-info-col">
                <h3 className="about-label">{t("about.mission.title")}</h3>
                <p className="about-text-small">{t("about.mission.text")}</p>
              </div>

              <div className="about-info-col">
                <h3 className="about-label">{t("about.contact.title")}</h3>
                <div className="about-contact-list">
                  <ContactItem icon={<Building2 size={16} />} text={t("about.contact.name")} />
                  <ContactItem icon={<MapPin size={16} />} text="Dolná Seč 70, 935 31, SK" />
                  <div className="contact-row">
                    <Mail size={16} className="lp-pink" />
                    <a href="mailto:olympeak.app@gmail.com" className="lp-link-small">
                      olympeak.app@gmail.com
                    </a>
                  </div>
                  <div className="about-ids-row">
                    <span>ID: 57 021 295</span>
                    <span className="divider">|</span>
                    <span>Tax: 1130584213</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <p>© {new Date().getFullYear()} Olympeak. {t("about.footer.rights")}</p>
          <div className="lp-footer-links">
            <a href="/about">{t("about.footer.about")}</a>
            <a href="/auth?mode=login">{t("about.footer.login")}</a>
            <a href="/privacy">{t("about.footer.privacy")}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ContactItem({ icon, text }) {
  return (
    <div className="contact-row">
      <span className="lp-pink">{icon}</span>
      <span className="contact-text">{text}</span>
    </div>
  );
}